/**
 * Semantic color palette — the "Option B" pattern we settled on 2026-09-09.
 *
 * Colors here MEAN something (funnel stage / role), not just decoration.
 * Every colored tile / chip / accent in the app should pull from this file
 * instead of hard-coding hex. That way the whole app "pops" consistently
 * and you can retune the palette in one place.
 *
 *   brand     · iSpeedToClose primary chrome / CTAs                → indigo
 *   cold      · Discover, new leads, cold analysis                 → sky
 *   warm      · Urgency, tasks, brand accent                       → amber
 *   hot       · Negotiation, in-flight offers, hot deals           → purple
 *   won       · Closed, revenue, cash in, success chips, Jarvis AI → emerald
 *   risk      · Alerts, stale, declined, missed follow-up          → rose
 *   info      · Neutral / meta (footer, timestamps)                → slate
 *
 * 2026-Feb: brand pivoted from warm gold → cool indigo (+ amber accent).
 * `warm` (amber) is now the ACCENT role, not the primary brand chrome.
 * `won` (emerald) is the AI-signature / success role, kept distinct from brand.
 */

// Each role has: `dot` (accent stripe/border), `bg` (icon-well fill),
// `fg` (icon glyph + accent text), and `chipBg/chipFg` for pill labels.
export const ROLE = {
  brand: {
    dot:    "#4F46E5",   // indigo-600
    bg:     "#E0E7FF",   // indigo-100
    fg:     "#3730A3",   // indigo-800
    chipBg: "#EEF2FF",   // indigo-50
    chipFg: "#4338CA",   // indigo-700
  },
  cold: {
    dot:    "#0EA5E9",   // sky-500 (was blue-500; shifted to sky so brand indigo stays dominant)
    bg:     "#E0F2FE",   // sky-100
    fg:     "#0369A1",   // sky-700
    chipBg: "#F0F9FF",   // sky-50
    chipFg: "#075985",   // sky-800
  },
  warm: {
    dot:    "#F59E0B",   // amber-500 (brand accent)
    bg:     "#FEF3C7",   // amber-100
    fg:     "#B45309",   // amber-700
    chipBg: "#FFFBEB",   // amber-50
    chipFg: "#92400E",   // amber-800
  },
  hot: {
    dot:    "#A855F7",   // purple-500
    bg:     "#F3E8FF",   // purple-100
    fg:     "#6B21A8",   // purple-800
    chipBg: "#FAF5FF",   // purple-50
    chipFg: "#581C87",   // purple-900
  },
  won: {
    dot:    "#10B981",   // emerald-500 (semantic success + Jarvis AI signature)
    bg:     "#D1FAE5",   // emerald-100
    fg:     "#065F46",   // emerald-800
    chipBg: "#ECFDF5",   // emerald-50
    chipFg: "#047857",   // emerald-700
  },
  risk: {
    dot:    "#F43F5E",   // rose-500
    bg:     "#FFE4E6",   // rose-100
    fg:     "#9F1239",   // rose-800
    chipBg: "#FFF1F2",   // rose-50
    chipFg: "#BE123C",   // rose-700
  },
  info: {
    dot:    "#94A3B8",   // slate-400
    bg:     "#F1F5F9",   // slate-100
    fg:     "#334155",   // slate-700
    chipBg: "#F8FAFC",   // slate-50
    chipFg: "#475569",   // slate-600
  },
};

// KPI-domain → role map. When a KPI wants a color and just knows its
// domain string, it can look up the semantic role here without every
// component re-inventing the association.
export const DOMAIN_ROLE = {
  financial:   "won",
  revenue:     "won",
  marketing:   "hot",
  acquisition: "cold",
  disposition: "hot",
  operational: "warm",
  negotiation: "hot",
  buyer:       "cold",
  portfolio:   "won",
  market:      "info",
  coach:       "warm",
  platform:    "info",
};

// Pipeline stage → role map. Discover→Analysis→Negotiation→Closed
// visually reads as blue→amber→purple→emerald (cold to won).
export const STAGE_ROLE = {
  lead:            "cold",
  new:             "cold",
  cold:            "cold",
  captured:        "cold",
  analyzing:       "warm",
  analysis:        "warm",
  review:          "warm",
  warm:            "warm",
  negotiating:     "hot",
  negotiation:     "hot",
  offer_made:      "hot",
  counteroffer:    "hot",
  hot:             "hot",
  under_contract:  "hot",
  pending:         "hot",
  closed:          "won",
  won:             "won",
  assigned:        "won",
  sold:            "won",
  settled:         "won",
  lost:            "risk",
  declined:        "risk",
  stale:           "risk",
};

export function roleFor({ domain, stage, fallback = "info" }) {
  if (stage) {
    const s = String(stage).toLowerCase().replace(/[\s-]+/g, "_");
    if (STAGE_ROLE[s]) return STAGE_ROLE[s];
  }
  if (domain && DOMAIN_ROLE[domain]) return DOMAIN_ROLE[domain];
  return fallback;
}
